import { HttpClient } from 'api/http-client';
import { ProjectId } from 'api/models';
import { FormInstanceDetails } from 'queries/form-instance';
import {
  FormAnswer,
  FormAnswerImage,
  FormInstance,
  FormInstanceAnswers,
  FormInstancesByProject,
  FormTemplateAnswers,
  WebFormData,
} from './form-instances.types';

const baseUrl = '/form-instances';

export type FormAnswerPayload = Pick<
  FormAnswer,
  'formInstanceId' | 'answerMetaJson'
>;
export interface WebFormAnswerPayload
  extends Pick<FormAnswer, 'formInstanceId' | 'answerMetaJson'> {
  answerId?: number;
}

export interface UploadFormAnswerImagePayload {
  file: File;
  attachment: {
    name: string;
    context: 'FORM';
  };
}

export class FormInstanceClient extends HttpClient {
  public createFormAnswer = (payload: FormAnswerPayload): Promise<FormAnswer> =>
    this.post('/answers', payload);

  public getFormAnswerById = (answerId: number): Promise<FormAnswer> =>
    this.get(`/answers/${answerId}`);

  public getFormInstanceById = (instanceId: number): Promise<FormInstance> =>
    this.get(`${baseUrl}/${instanceId}`);

  public getFormInstancesByProject = ([
    _,
    projectId,
  ]: ProjectId): Promise<FormInstancesByProject> =>
    this.get(`${baseUrl}/project/${projectId}`);

  public getAllAnswersByFormInstance = (
    instanceId: number,
  ): Promise<FormInstanceAnswers> =>
    this.get(`${baseUrl}/${instanceId}/all-answers`);

  public getAllAnswersByFormTemplate = (
    formTemplateId: number,
  ): Promise<FormTemplateAnswers> =>
    this.get(`/forms/${formTemplateId}/responses`);

  public getAllInstancesByFormTemplate = (
    formTemplateId: number,
  ): Promise<FormInstanceDetails> =>
    this.get(`/form-instances/template/${formTemplateId}`);

  public async uploadFormAnswerImage({
    file,
    attachment,
  }: UploadFormAnswerImagePayload): Promise<FormAnswerImage> {
    const formData = new FormData();
    const fileMeta = {
      attachment: {
        name: attachment.name,
        context: attachment.context,
      },
    };

    const json = JSON.stringify(fileMeta);
    const blob = new Blob([json], {
      type: 'application/json',
    });

    formData.append('file', file);
    formData.append('request', blob);

    return (
      await this.post<FormData, FormData, { attachment: FormAnswerImage }>(
        '/answers/attachment',
        formData,
      )
    ).attachment;
  }

  public updateFormAnswer = (
    formResponseId: number,
    payload: FormAnswerPayload,
  ): Promise<FormAnswer> => this.put(`/answers/${formResponseId}`, payload);

  public deleteFormAnswer = (formAnswerId: number) =>
    this.delete(`/answers/${formAnswerId}`);

  public getWebFormData = (instanceHash: string): Promise<WebFormData> =>
    this.get('/web-forms', {
      params: {
        hash: instanceHash,
      },
    });

  public createWebFormAnswer = (
    payload: WebFormAnswerPayload,
  ): Promise<FormAnswer> => this.post('/answers', payload);

  public updateWebFormAnswer = (
    payload: WebFormAnswerPayload,
  ): Promise<FormAnswer> => this.put('/answers/update', payload);

  public exportFormTemplateResponseAsCSV = (
    templateId: number,
  ): Promise<string> =>
    this.get<{ downloadUrl: string }>(
      `/forms/report/${templateId}/response`,
    ).then((response) => response.downloadUrl);

  public exportFormAnswersByInstance = (instanceId: number): Promise<string> =>
    this.get<{ downloadUrl: string }>(
      `/forms/report/response?formInstanceId=${instanceId}`,
    ).then((response) => response.downloadUrl);

  public exportFormAttachments = (
    attachmentIds: number[] = [],
  ): Promise<Blob> =>
    this.get(
      `forms/attachment/download?attachmentList=${attachmentIds.join(',')}`,
      {
        responseType: 'blob',
      },
    );

  public downloadFormAttachment(attachmentId: number) {
    return `${this.BASEURL}/forms/attachment/${attachmentId}/download`;
  }
}

export const FormInstanceApi = new FormInstanceClient();
